import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, catchError, map, finalize } from 'rxjs';

import DataResponse from '../data-response/data-response';
import { LocalStorageService } from '../local-storage/local-storage.service';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface User {
  id:                 number;
  name:               string;
  email:              string;
  role?:              string;
  company_name?:      string;
  company_id?:        number | null;
  mfa_enabled?:       boolean;
  /** 'email' | 'totp' | null — null when MFA is disabled */
  mfa_method?:        string | null;
  /** True when the company requires MFA but the user hasn't set it up yet */
  mfa_setup_required?: boolean;
  impersonating?:     boolean;
  reseller_id?:       number | null;
  reseller_name?:     string | null;
  impersonation?:     { by_reseller: boolean };
  /** True when the company allows users to invite colleagues (Immutec rollout flag). */
  user_invites_enabled?: boolean;
}

export interface LoginResult {
  status:        boolean;
  error:         HttpErrorResponse | null;
  mfa_required?: boolean;
  /** 'email' | 'totp' — only present when mfa_required is true */
  mfa_method?:   string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AuthService {
  // ── State ──────────────────────────────────────────────────────────────────
  private readonly _user       = signal<User | null>(null);
  private readonly _loggingIn  = signal(false);
  private readonly _loggingOut = signal(false);
  private loginResult: LoginResult = { status: false, error: null };

  // ── Public read-only signals ───────────────────────────────────────────────
  readonly user            = this._user.asReadonly();
  readonly loggingIn       = this._loggingIn.asReadonly();
  readonly loggingOut      = this._loggingOut.asReadonly();
  readonly isAuthenticated = computed(() => !!this._user());
  readonly isGlobalAdmin   = computed(() => {
    const u = this._user();
    return !!u && u.company_id == null && u.reseller_id == null && (u.role === 'admin' || u.role === 'engineer');
  });
  readonly isReseller = computed(() => {
    const u = this._user();
    return !!u && u.role === 'reseller' && u.reseller_id != null && u.company_id == null;
  });
  readonly isAdminArea = computed(() => this.isGlobalAdmin() || this.isReseller());
  readonly isReadOnlyImpersonation = computed(() => !!this._user()?.impersonation?.by_reseller);
  readonly canWrite = computed(() => this.isGlobalAdmin());
  readonly canInviteUsers = computed(() => !!this._user()?.user_invites_enabled);

  constructor(
    private readonly router:       Router,
    private readonly http:         HttpClient,
    private readonly localStorage: LocalStorageService,
  ) {}

  // ── Login ──────────────────────────────────────────────────────────────────
  public login(email: string, password: string): Observable<LoginResult> {
    this._loggingIn.set(true);

    return this.http.post('/api/auth/login', { email, password }).pipe(
      map((response: object): LoginResult => {
        // Check for MFA required response first
        const raw = response as { mfa_required?: boolean; mfa_method?: string };
        if (raw.mfa_required) {
          this.loginResult = { status: false, error: null, mfa_required: true, mfa_method: raw.mfa_method ?? 'email' };
          return this.loginResult;
        }
        const result = DataResponse.fromResponse<User>(response);
        if (result.data) {
          this._user.set(result.data);
        }
        this.loginResult = { status: true, error: null };
        return this.loginResult;
      }),
      catchError((err: HttpErrorResponse): Observable<LoginResult> => {
        return of({ status: false, error: err });
      }),
      finalize(() => this._loggingIn.set(false))
    );
  }

  // ── Check active session ───────────────────────────────────────────────────
  // Returns an Observable<boolean>; used by route guards.
  // When forced=false and a login was already confirmed this session, skips the
  // network call and returns true immediately.
  public isLoggedIn(forced = false): Observable<boolean> {
    if (this.loginResult.status && !forced) {
      return of(true);
    }

    return this.http.get('/api/auth/current').pipe(
      map((response: object) => {
        const result = DataResponse.fromResponse<User>(response);
        if (result.data) {
          this._user.set(result.data);
          this.loginResult = { status: true, error: null };
        }
        return result.data !== null;
      }),
      catchError(() => {
        this.loginResult = { status: false, error: null };
        return of(false);
      })
    );
  }

  // ── Logout ─────────────────────────────────────────────────────────────────
  public logout(): Observable<boolean> {
    this._loggingOut.set(true);

    return this.http.post('/api/auth/logout', null).pipe(
      map(() => true),
      catchError(() => of(false)),
      finalize(() => {
        this.registerLogout();
        this.router.navigate(['/login']);
      })
    );
  }

  public registerLogout(): void {
    this.loginResult = { status: false, error: null };
    this._loggingOut.set(false);
    this._user.set(null);
    this.localStorage.clear();
  }

  // ── Get current user (synchronous snapshot) ───────────────────────────────
  public getUser(): User | null {
    return this._user();
  }

  // ── Set user from API response ───────────────────────────────────────────
  public setUserFromResponse(user: User): void {
    this._user.set(user);
  }

  // ── Password reset request (step 1 — send link) ───────────────────────────
  public reset(email: string): Observable<boolean> {
    return this.http.post('/api/auth/forgotten', { email }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  // ── Password reset (step 2 — submit new password with token) ──────────────
  public resetPassword(payload: {
    token:                 string;
    email:                 string;
    password:              string;
    password_confirmation: string;
  }): Observable<{ ok: boolean; message: string }> {
    return this.http.post<{ message: string }>('/api/auth/reset', payload).pipe(
      map(res  => ({ ok: true,  message: res.message ?? 'Password reset successfully.' })),
      catchError(err => of({
        ok:      false,
        message: err?.error?.message ?? 'Something went wrong. Please try again.',
      }))
    );
  }

  // ── Update profile ────────────────────────────────────────────────────────
  public updateProfile(name: string, email: string): Observable<User | null> {
    return this.http.put('/api/user', { name, email }).pipe(
      map((response: object) => {
        const result = DataResponse.fromResponse<User>(response);
        if (result.data) this._user.set(result.data);
        return result.data;
      }),
      catchError(() => of(null))
    );
  }

  // ── Change password ────────────────────────────────────────────────────────
  public changePassword(
    current_password: string,
    password:         string,
    password_confirmation: string,
  ): Observable<{ ok: boolean; message: string }> {
    return this.http
      .put<{ message: string }>('/api/auth/password', { current_password, password, password_confirmation })
      .pipe(
        map(res  => ({ ok: true,  message: res.message ?? 'Password updated.' })),
        catchError(err => of({ ok: false, message: err?.error?.message ?? err?.error?.errors?.current_password?.[0] ?? 'Something went wrong.' }))
      );
  }

  // ── MFA send (setup, requires auth) ───────────────────────────────────────
  public mfaSend(): Observable<{ ok: boolean }> {
    return this.http.post('/api/auth/mfa/send', {}).pipe(
      map(() => ({ ok: true })),
      catchError(() => of({ ok: false }))
    );
  }

  // ── MFA enable ────────────────────────────────────────────────────────────
  public mfaEnable(code: string): Observable<{ ok: boolean; message: string }> {
    return this.http.post<{ message: string; data: User }>('/api/auth/mfa/enable', { code }).pipe(
      map(res => {
        if (res.data) this._user.set(res.data);
        return { ok: true, message: res.message ?? 'MFA enabled.' };
      }),
      catchError(err => of({ ok: false, message: err?.error?.message ?? 'Invalid code.' }))
    );
  }

  // ── MFA disable ───────────────────────────────────────────────────────────
  public mfaDisable(): Observable<{ ok: boolean; message: string }> {
    return this.http.post<{ message: string; data: User }>('/api/auth/mfa/disable', {}).pipe(
      map(res => {
        if (res.data) this._user.set(res.data);
        return { ok: true, message: res.message ?? 'MFA disabled.' };
      }),
      catchError(err => of({ ok: false, message: err?.error?.message ?? 'Something went wrong.' }))
    );
  }

  // ── TOTP setup — get secret + otpauth URI ─────────────────────────────────
  public totpSetup(): Observable<{ secret: string; otpauth: string } | null> {
    return this.http.get<{ secret: string; otpauth: string }>('/api/auth/mfa/totp/setup').pipe(
      map(res => res),
      catchError(() => of(null))
    );
  }

  // ── TOTP enable — verify first code to confirm setup ──────────────────────
  public totpEnable(code: string): Observable<{ ok: boolean; message: string }> {
    return this.http.post<{ message: string; data: User }>('/api/auth/mfa/totp/enable', { code }).pipe(
      map(res => {
        if (res.data) this._user.set(res.data);
        return { ok: true, message: res.message ?? 'Authenticator app connected.' };
      }),
      catchError(err => of({ ok: false, message: err?.error?.message ?? 'Invalid code.' }))
    );
  }

  // ── MFA challenge send (during login) ─────────────────────────────────────
  public mfaChallengeSend(): Observable<{ ok: boolean }> {
    return this.http.post('/api/auth/mfa/challenge/send', {}).pipe(
      map(() => ({ ok: true })),
      catchError(() => of({ ok: false }))
    );
  }

  // ── MFA challenge verify (during login) ───────────────────────────────────
  public mfaChallenge(code: string, rememberDevice = false): Observable<LoginResult> {
    return this.http.post<{ data: User; device_token?: string }>(
      '/api/auth/mfa/challenge',
      { code, remember_device: rememberDevice }
    ).pipe(
      map((response) => {
        // Persist trusted-device token as a cookie if backend returned one.
        // Secure on HTTPS so the MFA-skipping token never travels over plain
        // HTTP in staging/production.
        if (response.device_token) {
          const maxAge = 60 * 60 * 24 * 30; // 30 days in seconds
          const secure = location.protocol === 'https:' ? '; Secure' : '';
          document.cookie = `trusted_device=${response.device_token}; max-age=${maxAge}; path=/; SameSite=Lax${secure}`;
        }
        const result = DataResponse.fromResponse<User>(response);
        if (result.data) {
          this._user.set(result.data);
          this.loginResult = { status: true, error: null };
        }
        return this.loginResult;
      }),
      catchError((err: HttpErrorResponse): Observable<LoginResult> => {
        return of({ status: false, error: err });
      })
    );
  }

}
